import { useNuxtApp, useRoute } from 'nuxt/app';
import type { SharedPostHogEventName } from '../../types/posthog';

/**
 * Factory that creates a type-safe `usePostHog` composable bound to the
 * caller's event name union.  Each app calls this once with its own
 * `PostHogEventName` type and re-exports the result as `usePostHog`.
 *
 * @example
 * // projects/client/app/composables/usePostHog.ts
 * import { createUsePostHog } from '#bubba/shared/composables/usePostHog';
 * type PostHogEventName = SharedPostHogEventName | 'search_result_clicked';
 * export const usePostHog = createUsePostHog<PostHogEventName>();
 */
export function createUsePostHog<TEventName extends string = SharedPostHogEventName>() {
  return function usePostHog() {
    const getPostHog = () => {
      if (!import.meta.client) return null;
      try {
        const nuxtApp = useNuxtApp() as unknown as {
          $posthog: () => import('posthog-js').PostHog | null;
        };
        return nuxtApp.$posthog();
      } catch {
        return null;
      }
    };

    /**
     * Capture a named event with optional properties.
     */
    const captureEvent = (
      eventName: TEventName,
      properties?: Record<string, string | number | boolean | null | undefined>,
    ) => {
      if (!import.meta.client) return;
      const posthog = getPostHog();
      if (!posthog) return;
      posthog.capture(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    };

    /**
     * Manually capture a page view (auto-capture is disabled in the plugin).
     */
    const capturePageView = () => {
      if (!import.meta.client) return;
      const posthog = getPostHog();
      if (!posthog) return;
      const route = useRoute();
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        path: route.path,
      });
    };

    /**
     * Identify a logged-in user. Call after login / session restore.
     */
    const identifyUser = (
      distinctId: string,
      properties?: Record<string, string | number | boolean | null | undefined>,
    ) => {
      if (!import.meta.client) return;
      const posthog = getPostHog();
      if (!posthog) return;
      posthog.identify(distinctId, properties);
    };

    /**
     * Reset identity on logout.
     */
    const resetUser = () => {
      if (!import.meta.client) return;
      const posthog = getPostHog();
      if (!posthog) return;
      posthog.reset();
    };

    /**
     * Associate user with a group (e.g. partner/company).
     */
    const setGroup = (
      groupType: string,
      groupKey: string,
      properties?: Record<string, string | number | boolean | null | undefined>,
    ) => {
      if (!import.meta.client) return;
      const posthog = getPostHog();
      if (!posthog) return;
      posthog.group(groupType, groupKey, properties);
    };

    /**
     * Capture a client-side error event with contextual metadata.
     */
    const captureError = (errorData: {
      error: string | Error;
      context?: string;
      location?: string;
      userAction?: string;
      severity?: 'low' | 'medium' | 'high' | 'critical';
      metadata?: Record<string, string | number | boolean | null | undefined>;
    }) => {
      if (!import.meta.client) return;
      const posthog = getPostHog();
      if (!posthog) return;

      const errorMessage =
        typeof errorData.error === 'string'
          ? errorData.error
          : errorData.error.message || 'Unknown error';

      const route = useRoute();
      posthog.capture('error_occurred', {
        error_message: errorMessage,
        error_context: errorData.context,
        error_location: errorData.location || route.path,
        user_action: errorData.userAction,
        severity: errorData.severity || 'medium',
        timestamp: new Date().toISOString(),
        ...errorData.metadata,
      });
    };

    return {
      captureEvent,
      capturePageView,
      identifyUser,
      resetUser,
      setGroup,
      captureError,
    };
  };
}
